import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import { ALIGNMENT } from 'models/Global';
import MymeTypeIcon from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/MymeTypeIcon';
import TruckIcon from 'icons/Truck.icon';
import MinusCircleIcon from 'icons/MinusCircle.icon';
import useCallbackRef from 'hooks/useCallbackRef';
import useCallbackAsync from 'hooks/useCallbackAsync';
import useDownloadFile from 'hooks/useDownloadFile';
import confirm from 'modules/confirm';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { getFormattedDate } from 'dateUtils';
import { FileTypeRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import {
  getIsRfiFileStatusAdded,
  getIsRfiFileStatusIgnored,
  getIsRfiFileStatusUploaded,
} from 'router/subrouters/DataRoom/utils';
import { getHasElementsInArray } from 'utils';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import FilePreviewModal from 'components/FilePreviewModal';
import { AddDocToDataRoomModal } from '../../../../../RfiList/components/AddDocToDataRoomModal';

import './RfiCategoryDrawerFile.styles.scss';

type RfiCategoryDrawerFileProps = {
  className?: string;
  file: FileTypeRFI;
  categoryId: string;
  onFileUpdate: (file: FileTypeRFI) => void;
};

const RfiCategoryDrawerFile: React.FC<RfiCategoryDrawerFileProps> = (props) => {
  const { className, file, categoryId, onFileUpdate } = props;

  const { t } = useTranslation();

  const [addToDrModal, addToDrModalRef] = useCallbackRef<ModalRef>();
  const [previewModal, previewModalRef] = useCallbackRef<OverlayRef>();

  const isUploaded = getIsRfiFileStatusUploaded(file.status);
  const isAdded = getIsRfiFileStatusAdded(file.status);
  const isIgnored = getIsRfiFileStatusIgnored(file.status);

  const onAddOrMoveClick = useCallback(
    () => addToDrModal?.open(),
    [addToDrModal],
  );

  const downloadFile = useDownloadFile();

  const onDownload = useCallback(
    () =>
      downloadFile({
        id: file._id,
        url: file.file?.url,
        name: file.file?.name,
      }),
    [downloadFile, file._id, file.file?.url, file.file?.name],
  );

  const [onRemoveOrIgnore, loading] = useCallbackAsync(async () => {
    const confirmed = await confirm({
      type: 'delete',
      title: t(isAdded ? 'deleteDocumentTitle' : 'ignoreDocumentTitle'),
      content: t(
        isAdded ? 'deleteDocumentDescription' : 'ignoreDocumentDescription',
        { documentName: file.file?.name },
      ),
      submitBtnText: t(isAdded ? 'delete' : 'confirm'),
      cancelBtnText: t('cancel'),
    });

    if (!confirmed) return;

    const { data } = await dataRoomApi.rfi[
      isAdded ? 'deleteDocument' : 'ignoreDocument'
    ](categoryId, file._id);

    onFileUpdate(data);
  });

  const options = useMemo(
    () => [
      ...(isUploaded
        ? [
            {
              label: t('addToDataRoom'),
              icon: <TruckIcon />,
              onClick: onAddOrMoveClick,
            },
            {
              label: t('ignore'),
              icon: <MinusCircleIcon />,
              alert: true,
              disabled: loading,
              onClick: onRemoveOrIgnore,
            },
          ]
        : []),
      ...(isAdded
        ? [
            {
              label: t('moveWithinDR'),
              icon: <TruckIcon />,
              onClick: onAddOrMoveClick,
            },
            {
              label: t('removeFromDR'),
              icon: <MinusCircleIcon />,
              alert: true,
              disabled: loading,
              onClick: onRemoveOrIgnore,
            },
          ]
        : []),
    ],
    [t, isUploaded, isAdded, loading, onAddOrMoveClick, onRemoveOrIgnore],
  );

  return (
    <li className={classNames('rfi-category-drawer-file', className)}>
      <div className="rfi-category-drawer-file__header">
        <MymeTypeIcon mimeType={file.file?.type} />
        <p
          className="rfi-category-drawer-file__name"
          onClick={() => previewModal?.open()}
        >
          {file.file?.name}
        </p>

        {getHasElementsInArray(options) && (
          <ThreeDotsDropdown
            options={options}
            alignment={ALIGNMENT.VERTICAL}
            withBorder={false}
            renderAsPortal
          />
        )}
      </div>

      <div className="rfi-category-drawer-file__footer">
        <p className="rfi-category-drawer-file__date">
          {file.dataRoomRFIFileUploader?.email && (
            <span>{file.dataRoomRFIFileUploader.email}</span>
          )}
          <span
            className={classNames({
              'rfi-category-drawer-file__date__bullet':
                file.dataRoomRFIFileUploader?.email,
            })}
          >
            {getFormattedDate(file.createdAt)}
          </span>
        </p>

        <span
          className={classNames('rfi-category-drawer-file__status', {
            added: isAdded,
            ignored: isIgnored,
          })}
        >
          {t(isAdded ? 'inDataRoom' : isIgnored ? 'ignored' : 'notInDataRoom')}
        </span>
      </div>

      <AddDocToDataRoomModal
        ref={addToDrModalRef}
        document={file}
        dataRoomRfiCategoryId={categoryId}
        onAddSuccess={onFileUpdate}
      />

      <FilePreviewModal
        ref={previewModalRef}
        url={file.file?.url}
        type={file.file?.type}
        isDownloadDisabled={!file.file?.url}
        downloadResetKey={file._id}
        onDownload={onDownload}
      />
    </li>
  );
};

export default RfiCategoryDrawerFile;
